import { useMemo, useState, type FormEvent } from 'react';
import { isAxiosError } from 'axios';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuthStore, useHasPermission } from '@/auth/auth-store';
import { useT } from '@/i18n/use-t';
import { apiBaseUrl } from '@/lib/api-client';
import { platformBaseUrlForLoyalty } from '@/lib/platform-base-url';
import {
  useCreateServiceClient,
  useRevokeServiceClient,
  useServiceClients,
  type CreatedServiceClient,
  type ServiceClient,
} from '@/hooks/use-service-clients';
import {
  useMasterkasaSources,
  useRunMasterkasaImport,
  type MasterkasaSource,
} from '@/hooks/use-masterkasa';
import { MasterkasaPromotionsDialog } from './masterkasa-promotions-dialog';
import { useIp2Sources, type Ip2Source } from '@/hooks/use-ip2-sources';
import { useIp3Sources, type Ip3Source } from '@/hooks/use-ip3-sources';
import { useIp4Sources, type Ip4Source } from '@/hooks/use-ip4-sources';
import { ConnectorLocationSettingsPanel } from './connector-location-settings-panel';
import { CrmReceiptStatusPanel } from './crm-receipt-status-panel';
import { Ip2SourceDialog } from './ip2-source-dialog';
import { Ip3SourceDialog } from './ip3-source-dialog';
import { Ip4SourceDialog } from './ip4-source-dialog';
import { DocumentFeedStatusPanel } from './document-feed-status-panel';
import { ExportStatusPanel } from './export-status-panel';
import { MasterkasaSourceDialog } from './masterkasa-source-dialog';

function errorMessage(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const body = error.response?.data as { message?: string } | undefined;
    return body?.message ?? error.message;
  }
  return error instanceof Error ? error.message : fallback;
}

function displayDate(value: string | null, emptyValue: string): string {
  if (value === null) return emptyValue;
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(value),
  );
}

/** Which credential section a create / revoke failure belongs under (RL-369). */
type CredentialSection = 'loyalty' | 'apiKey';

function sectionOf(client: ServiceClient): CredentialSection {
  return client.scopes.includes('gp.write') ? 'apiKey' : 'loyalty';
}

async function copyValue(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

export function IntegrationsPage() {
  const t = useT('admin_ui');
  const canManage = useHasPermission('integration.admin');
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.accessToken);
  const secureScaleLicensed = user?.modules?.includes('securescale') === true;
  const secureScaleSession = useMemo(
    () => ({
      key: crypto.randomUUID(),
      queries: new QueryClient({
        defaultOptions: { queries: { retry: false }, mutations: { retry: false } },
      }),
    }),
    [user?.id, token, secureScaleLicensed, canManage],
  );
  const canManageMasterkasa = useHasPermission('connectors.admin');
  const clientsQuery = useServiceClients(canManage);
  const createClient = useCreateServiceClient();
  const revokeClient = useRevokeServiceClient();
  const [name, setName] = useState('');
  const [created, setCreated] = useState<CreatedServiceClient | null>(null);
  const [apiKeyName, setApiKeyName] = useState('');
  const [createdApiKey, setCreatedApiKey] = useState<CreatedServiceClient | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ServiceClient | null>(null);
  const [error, setError] = useState<{ section: CredentialSection; message: string } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const masterkasaQuery = useMasterkasaSources(canManageMasterkasa);
  const runMasterkasa = useRunMasterkasaImport();
  const [replayIds, setReplayIds] = useState<Record<string, string>>({});
  const [sourceDialog, setSourceDialog] = useState<
    { mode: 'create' } | { mode: 'settings'; source: MasterkasaSource } | null
  >(null);
  const [promotionsSource, setPromotionsSource] = useState<MasterkasaSource | null>(null);
  const ip2Query = useIp2Sources(canManageMasterkasa);
  const [ip2Dialog, setIp2Dialog] = useState<{ source: Ip2Source | null } | null>(null);
  const ip3Query = useIp3Sources(canManageMasterkasa);
  const [ip3Dialog, setIp3Dialog] = useState<{ source: Ip3Source | null } | null>(null);
  const ip4Query = useIp4Sources(canManageMasterkasa);
  const [ip4Dialog, setIp4Dialog] = useState<{ source: Ip4Source | null } | null>(null);

  const platformUrl = platformBaseUrlForLoyalty(apiBaseUrl, globalThis.window.location.origin);
  const loyaltyClients = useMemo(
    () => (clientsQuery.data ?? []).filter((client) => client.scopes.includes('sync.read')),
    [clientsQuery.data],
  );
  const universalApiClients = useMemo(
    () => (clientsQuery.data ?? []).filter((client) => client.scopes.includes('gp.write')),
    [clientsQuery.data],
  );

  async function copy(label: string, value: string): Promise<void> {
    if (await copyValue(value)) {
      setCopied(label);
      globalThis.setTimeout(() => setCopied(null), 1_500);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const cleanName = name.trim();
    if (cleanName === '') return;
    setError(null);
    try {
      setCreated(await createClient.mutateAsync({ name: cleanName, scopes: ['sync.read'] }));
      setName('');
    } catch (submitError) {
      setError({
        section: 'loyalty',
        message: errorMessage(submitError, t('settings.integrations.create.error')),
      });
    }
  }

  async function submitApiKey(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const cleanName = apiKeyName.trim();
    if (cleanName === '') return;
    setError(null);
    try {
      const result = await createClient.mutateAsync({ name: cleanName, scopes: ['gp.write'] });
      if (result.apiKey === undefined) {
        throw new Error(
          t('settings.integrations.apiKeys.missing', 'API-key credential was not returned'),
        );
      }
      setCreatedApiKey(result);
      setApiKeyName('');
    } catch (submitError) {
      setError({
        section: 'apiKey',
        message: errorMessage(submitError, t('settings.integrations.create.error')),
      });
    }
  }

  async function revoke(): Promise<void> {
    if (revokeTarget === null) return;
    const section = sectionOf(revokeTarget);
    setError(null);
    try {
      await revokeClient.mutateAsync(revokeTarget.id);
      setRevokeTarget(null);
    } catch (revokeError) {
      setError({
        section,
        message: errorMessage(revokeError, t('settings.integrations.revoke.error')),
      });
    }
  }

  if (!canManage && !canManageMasterkasa) {
    return (
      <div className="max-w-4xl space-y-2">
        <h1 className="text-lg font-semibold">{t('settings.integrations.title')}</h1>
        <p className="text-sm text-destructive">{t('settings.integrations.forbidden')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-6">
      <header>
        <h1 className="text-lg font-semibold">{t('settings.integrations.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('settings.integrations.description')}</p>
      </header>

      {canManage && secureScaleLicensed && (
        <QueryClientProvider client={secureScaleSession.queries}>
          <SecureScaleCredentialCard key={secureScaleSession.key} />
        </QueryClientProvider>
      )}

      {canManageMasterkasa && (
        <section className="space-y-5 rounded-lg border border-border bg-card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-primary">
                {t('settings.integrations.masterkasa.title')}
              </h2>
              <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
                {t('settings.integrations.masterkasa.description')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{t('settings.integrations.masterkasa.direction')}</Badge>
              <Button type="button" onClick={() => setSourceDialog({ mode: 'create' })}>
                {t('settings.integrations.masterkasa.addSource')}
              </Button>
            </div>
          </div>

          {masterkasaQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">
              {t('settings.integrations.masterkasa.loading')}
            </p>
          ) : masterkasaQuery.isError ? (
            <p className="text-sm text-destructive">
              {t('settings.integrations.masterkasa.loadError')}
            </p>
          ) : (masterkasaQuery.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('settings.integrations.masterkasa.empty')}
            </p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>{t('settings.integrations.masterkasa.source')}</TH>
                  <TH>{t('settings.integrations.masterkasa.status')}</TH>
                  <TH>{t('settings.integrations.masterkasa.cursor')}</TH>
                  <TH>{t('settings.integrations.masterkasa.replayFrom')}</TH>
                  <TH className="text-right">{t('settings.integrations.actions')}</TH>
                </TR>
              </THead>
              <TBody>
                {(masterkasaQuery.data ?? []).map((source) => {
                  const replayText = replayIds[source.id] ?? '';
                  const replayId = Number(replayText);
                  const canReplay =
                    source.activeGeneration !== null &&
                    replayText.trim() !== '' &&
                    Number.isSafeInteger(replayId) &&
                    replayId >= 0;
                  return (
                    <TR key={source.id}>
                      <TD>
                        <div className="font-medium">{source.location.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {source.name} · {source.host}
                        </div>
                      </TD>
                      <TD>
                        <Badge
                          variant={
                            source.importStatus === 'ok'
                              ? 'success'
                              : source.importStatus === 'failed'
                                ? 'destructive'
                                : 'outline'
                          }
                        >
                          {t(`settings.integrations.masterkasa.status.${source.importStatus}`)}
                        </Badge>
                        {source.catalogueWarning !== null &&
                          source.catalogueWarning !== undefined && (
                            <p
                              role="status"
                              className="mt-1 text-xs text-amber-700 dark:text-amber-400"
                            >
                              {source.catalogueWarning}
                            </p>
                          )}
                        {source.cashierWarning !== null && source.cashierWarning !== undefined && (
                          <p
                            role="status"
                            className="mt-1 text-xs text-amber-700 dark:text-amber-400"
                          >
                            {source.cashierWarning}
                          </p>
                        )}
                        {source.promotionObjects !== undefined &&
                          source.promotionObjects.mapped + source.promotionObjects.unmapped > 0 && (
                            <div className="mt-1 text-xs text-muted-foreground">
                              {source.promotionObjects.mapped}{' '}
                              {t('settings.integrations.masterkasa.promotions.mapped')} ·{' '}
                              {source.promotionObjects.unmapped}{' '}
                              {t('settings.integrations.masterkasa.promotions.unmapped')}
                            </div>
                          )}
                      </TD>
                      <TD className="font-mono text-xs">{source.lastProcessedSynchroId}</TD>
                      <TD>
                        <Input
                          aria-label={t('settings.integrations.masterkasa.replayFrom')}
                          inputMode="numeric"
                          value={replayText}
                          onChange={(event) =>
                            setReplayIds((current) => ({
                              ...current,
                              [source.id]: event.target.value,
                            }))
                          }
                          className="w-40 font-mono"
                        />
                      </TD>
                      <TD>
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setSourceDialog({ mode: 'settings', source })}
                          >
                            {t('settings.integrations.masterkasa.settings')}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setPromotionsSource(source)}
                          >
                            {t('settings.integrations.masterkasa.promotions.open')}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            disabled={runMasterkasa.isPending}
                            onClick={() =>
                              runMasterkasa.mutate({ sourceId: source.id, mode: 'full' })
                            }
                          >
                            {t('settings.integrations.masterkasa.fullImport')}
                          </Button>
                          <Button
                            type="button"
                            disabled={!canReplay || runMasterkasa.isPending}
                            onClick={() =>
                              runMasterkasa.mutate({
                                sourceId: source.id,
                                mode: 'incremental',
                                startSynchroId: replayId,
                              })
                            }
                          >
                            {t('settings.integrations.masterkasa.replay')}
                          </Button>
                        </div>
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          )}
          {runMasterkasa.isError && (
            <p className="text-sm text-destructive">
              {t('settings.integrations.masterkasa.runError')}
            </p>
          )}

          <ConnectorLocationSettingsPanel
            canEdit={canManageMasterkasa}
            secureScaleLicensed={secureScaleLicensed}
          />
          <ExportStatusPanel canEdit={canManageMasterkasa} />
        </section>
      )}

      {canManageMasterkasa && (
        <section className="space-y-5 rounded-lg border border-border bg-card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-primary">
                {t('settings.integrations.ip2.title')}
              </h2>
              <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
                {t('settings.integrations.ip2.description')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{t('settings.integrations.ip2.runsOn')}</Badge>
              <Button type="button" onClick={() => setIp2Dialog({ source: null })}>
                {t('settings.integrations.ip2.addSource')}
              </Button>
            </div>
          </div>

          {ip2Query.isLoading ? (
            <p className="text-sm text-muted-foreground">
              {t('settings.integrations.masterkasa.loading')}
            </p>
          ) : ip2Query.isError ? (
            <p className="text-sm text-destructive">{t('settings.integrations.ip2.loadError')}</p>
          ) : (ip2Query.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('settings.integrations.ip2.empty')}</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>{t('settings.integrations.masterkasa.source')}</TH>
                  <TH>{t('settings.integrations.status')}</TH>
                  <TH className="text-right">{t('settings.integrations.actions')}</TH>
                </TR>
              </THead>
              <TBody>
                {(ip2Query.data ?? []).map((source) => (
                  <TR key={source.id}>
                    <TD>
                      <div className="font-medium">{source.location.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {source.name} · {source.settings.server}
                      </div>
                    </TD>
                    <TD>
                      <Badge variant={source.enabled ? 'success' : 'outline'}>
                        {source.enabled
                          ? t('settings.integrations.ip2.enabled')
                          : t('settings.integrations.ip2.disabled')}
                      </Badge>
                    </TD>
                    <TD className="text-right">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIp2Dialog({ source })}
                      >
                        {t('settings.integrations.masterkasa.settings')}
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </section>
      )}

      {canManageMasterkasa && (
        <section className="space-y-5 rounded-lg border border-border bg-card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-primary">
                {t('settings.integrations.ip3.title')}
              </h2>
              <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
                {t('settings.integrations.ip3.description')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{t('settings.integrations.ip2.runsOn')}</Badge>
              <Button type="button" onClick={() => setIp3Dialog({ source: null })}>
                {t('settings.integrations.ip2.addSource')}
              </Button>
            </div>
          </div>

          {ip3Query.isLoading ? (
            <p className="text-sm text-muted-foreground">
              {t('settings.integrations.masterkasa.loading')}
            </p>
          ) : ip3Query.isError ? (
            <p className="text-sm text-destructive">{t('settings.integrations.ip3.loadError')}</p>
          ) : (ip3Query.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('settings.integrations.ip3.empty')}</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>{t('settings.integrations.masterkasa.source')}</TH>
                  <TH>{t('settings.integrations.status')}</TH>
                  <TH className="text-right">{t('settings.integrations.actions')}</TH>
                </TR>
              </THead>
              <TBody>
                {(ip3Query.data ?? []).map((source) => (
                  <TR key={source.id}>
                    <TD>
                      <div className="font-medium">{source.location.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {source.name} · {source.settings.tradePointCode} · {source.username}
                      </div>
                    </TD>
                    <TD>
                      <div className="flex flex-wrap gap-1">
                        <Badge variant={source.enabled ? 'success' : 'outline'}>
                          {source.enabled
                            ? t('settings.integrations.ip2.enabled')
                            : t('settings.integrations.ip2.disabled')}
                        </Badge>
                        {source.settings.allowInsecureHttp && (
                          <Badge variant="destructive">
                            {t('settings.integrations.ip3.insecureHttp')}
                          </Badge>
                        )}
                      </div>
                    </TD>
                    <TD className="text-right">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIp3Dialog({ source })}
                      >
                        {t('settings.integrations.masterkasa.settings')}
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
          {/* RL-936: the CRM receipt outbox, as the store's Link last reported it. */}
          {(ip3Query.data ?? []).length > 0 && (
            <CrmReceiptStatusPanel sources={ip3Query.data ?? []} />
          )}
        </section>
      )}

      {canManageMasterkasa && (
        <section className="space-y-5 rounded-lg border border-border bg-card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-primary">
                {t('settings.integrations.ip4.title')}
              </h2>
              <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
                {t('settings.integrations.ip4.description')}
              </p>
              <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
                {t('settings.integrations.ip4.pauseHint')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{t('settings.integrations.ip2.runsOn')}</Badge>
              <Button type="button" onClick={() => setIp4Dialog({ source: null })}>
                {t('settings.integrations.ip2.addSource')}
              </Button>
            </div>
          </div>

          {ip4Query.isLoading ? (
            <p className="text-sm text-muted-foreground">
              {t('settings.integrations.masterkasa.loading')}
            </p>
          ) : ip4Query.isError ? (
            <p className="text-sm text-destructive">{t('settings.integrations.ip4.loadError')}</p>
          ) : (ip4Query.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('settings.integrations.ip4.empty')}</p>
          ) : (
            <>
              <Table>
                <THead>
                  <TR>
                    <TH>{t('settings.integrations.masterkasa.source')}</TH>
                    <TH>{t('settings.integrations.status')}</TH>
                    <TH className="text-right">{t('settings.integrations.actions')}</TH>
                  </TR>
                </THead>
                <TBody>
                  {(ip4Query.data ?? []).map((source) => {
                    const queues = Object.values(source.feed?.queues ?? {});
                    return (
                      <TR key={source.id}>
                        <TD>
                          <div className="font-medium">{source.location.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {source.name} · {source.connection.protocol}://{source.connection.host}:
                            {source.connection.port} · {source.connection.credentials.username}
                          </div>
                        </TD>
                        <TD>
                          <div className="flex flex-wrap gap-1">
                            <Badge variant={source.enabled ? 'success' : 'outline'}>
                              {source.enabled
                                ? t('settings.integrations.ip2.enabled')
                                : t('settings.integrations.ip4.paused')}
                            </Badge>
                            <Badge variant="outline">
                              {t('settings.integrations.ip4.queuesOn')}:{' '}
                              {queues.filter((queue) => queue.enabled).length} / {queues.length}
                            </Badge>
                          </div>
                        </TD>
                        <TD className="text-right">
                          <Button
                            type="button"
                            variant="outline"
                            aria-label={`${t('settings.integrations.masterkasa.settings')} ${source.location.name}`}
                            onClick={() => setIp4Dialog({ source })}
                          >
                            {t('settings.integrations.masterkasa.settings')}
                          </Button>
                        </TD>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
              <DocumentFeedStatusPanel
                sources={ip4Query.data ?? []}
                canEdit={canManageMasterkasa}
              />
            </>
          )}
        </section>
      )}

      {canManage && (
        <section className="space-y-5 rounded-lg border border-border bg-card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-primary">
                {t('settings.integrations.loyalty.title')}
              </h2>
              <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
                {t('settings.integrations.loyalty.description')}
              </p>
            </div>
            <Badge variant="outline">{t('settings.integrations.direction')}</Badge>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="platform-base-url">{t('settings.integrations.platformUrl')}</Label>
            <div className="flex gap-2">
              <Input id="platform-base-url" readOnly value={platformUrl} className="font-mono" />
              <Button type="button" variant="outline" onClick={() => copy('url', platformUrl)}>
                {copied === 'url'
                  ? t('settings.integrations.copied')
                  : t('settings.integrations.copy')}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('settings.integrations.platformUrl.help')}
            </p>
          </div>

          <form onSubmit={submit} className="space-y-3 border-t border-border pt-5">
            <div>
              <h3 className="text-sm font-semibold">
                {t('settings.integrations.credential.title')}
              </h3>
              <p className="text-xs text-muted-foreground">
                {t('settings.integrations.credential.description')}
              </p>
            </div>
            <div className="flex max-w-xl items-end gap-2">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="credential-name">
                  {t('settings.integrations.credential.name')}
                </Label>
                <Input
                  id="credential-name"
                  value={name}
                  placeholder={t('settings.integrations.credential.defaultName')}
                  onChange={(event) => setName(event.target.value)}
                  maxLength={100}
                />
              </div>
              <Button type="submit" disabled={name.trim() === '' || createClient.isPending}>
                {createClient.isPending
                  ? t('settings.integrations.credential.creating')
                  : t('settings.integrations.credential.create')}
              </Button>
            </div>
          </form>

          {error?.section === 'loyalty' && (
            <p className="text-sm text-destructive">{error.message}</p>
          )}

          <div className="space-y-2 border-t border-border pt-5">
            <h3 className="text-sm font-semibold">
              {t('settings.integrations.credentials.title')}
            </h3>
            {clientsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">
                {t('settings.integrations.credentials.loading')}
              </p>
            ) : clientsQuery.isError ? (
              <p className="text-sm text-destructive">
                {errorMessage(clientsQuery.error, t('settings.integrations.credentials.error'))}
              </p>
            ) : loyaltyClients.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('settings.integrations.credentials.empty')}
              </p>
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>{t('settings.integrations.credential.name')}</TH>
                    <TH>{t('settings.integrations.clientId')}</TH>
                    <TH>{t('settings.integrations.status')}</TH>
                    <TH>{t('settings.integrations.lastUsed')}</TH>
                    <TH className="text-right">{t('settings.integrations.actions')}</TH>
                  </TR>
                </THead>
                <TBody>
                  {loyaltyClients.map((client) => (
                    <TR key={client.id}>
                      <TD>{client.name}</TD>
                      <TD className="font-mono text-xs">{client.id}</TD>
                      <TD>
                        <Badge variant={client.status === 'active' ? 'success' : 'destructive'}>
                          {client.status === 'active'
                            ? t('settings.integrations.status.active')
                            : t('settings.integrations.status.revoked')}
                        </Badge>
                      </TD>
                      <TD>{displayDate(client.lastUsedAt, t('settings.integrations.never'))}</TD>
                      <TD className="text-right">
                        {client.status === 'active' && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setRevokeTarget(client)}
                          >
                            {t('settings.integrations.revoke')}
                          </Button>
                        )}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </div>
        </section>
      )}

      {canManage && (
        <section className="space-y-5 rounded-lg border border-border bg-card p-5">
          <div>
            <h2 className="text-base font-semibold text-primary">
              {t('settings.integrations.apiKeys.title')}
            </h2>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
              {t('settings.integrations.apiKeys.description')}
            </p>
          </div>

          <form onSubmit={submitApiKey} className="space-y-3 border-t border-border pt-5">
            <div className="flex max-w-xl items-end gap-2">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="api-key-name">{t('settings.integrations.apiKeys.name')}</Label>
                <Input
                  id="api-key-name"
                  value={apiKeyName}
                  placeholder={t('settings.integrations.apiKeys.defaultName')}
                  onChange={(event) => setApiKeyName(event.target.value)}
                  maxLength={100}
                />
              </div>
              <Button type="submit" disabled={apiKeyName.trim() === '' || createClient.isPending}>
                {createClient.isPending
                  ? t('settings.integrations.apiKeys.creating')
                  : t('settings.integrations.apiKeys.create')}
              </Button>
            </div>
          </form>

          {error?.section === 'apiKey' && (
            <p className="text-sm text-destructive">{error.message}</p>
          )}

          <div className="space-y-2 border-t border-border pt-5">
            <h3 className="text-sm font-semibold">
              {t('settings.integrations.apiKeys.list.title')}
            </h3>
            {clientsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">
                {t('settings.integrations.credentials.loading')}
              </p>
            ) : clientsQuery.isError ? (
              <p className="text-sm text-destructive">
                {errorMessage(clientsQuery.error, t('settings.integrations.credentials.error'))}
              </p>
            ) : universalApiClients.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('settings.integrations.apiKeys.list.empty')}
              </p>
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>{t('settings.integrations.apiKeys.name')}</TH>
                    <TH>{t('settings.integrations.status')}</TH>
                    <TH>{t('settings.integrations.lastUsed')}</TH>
                    <TH className="text-right">{t('settings.integrations.actions')}</TH>
                  </TR>
                </THead>
                <TBody>
                  {universalApiClients.map((client) => (
                    <TR key={client.id}>
                      <TD>{client.name}</TD>
                      <TD>
                        <Badge variant={client.status === 'active' ? 'success' : 'destructive'}>
                          {client.status === 'active'
                            ? t('settings.integrations.status.active')
                            : t('settings.integrations.status.revoked')}
                        </Badge>
                      </TD>
                      <TD>{displayDate(client.lastUsedAt, t('settings.integrations.never'))}</TD>
                      <TD className="text-right">
                        {client.status === 'active' && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setRevokeTarget(client)}
                          >
                            {t('settings.integrations.revoke')}
                          </Button>
                        )}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </div>
        </section>
      )}

      {promotionsSource !== null && (
        <MasterkasaPromotionsDialog
          source={promotionsSource}
          onClose={() => setPromotionsSource(null)}
        />
      )}

      {ip2Dialog !== null && (
        <Ip2SourceDialog source={ip2Dialog.source} onClose={() => setIp2Dialog(null)} />
      )}

      {ip3Dialog !== null && (
        <Ip3SourceDialog
          source={ip3Dialog.source}
          enabledSibling={
            (ip3Query.data ?? []).find(
              (other) =>
                other.enabled &&
                ip3Dialog.source !== null &&
                other.id !== ip3Dialog.source.id &&
                other.location.id === ip3Dialog.source.location.id,
            ) ?? null
          }
          onClose={() => setIp3Dialog(null)}
        />
      )}

      {ip4Dialog !== null && (
        <Ip4SourceDialog
          source={ip4Dialog.source}
          takenLocationIds={(ip4Query.data ?? []).map((other) => other.location.id)}
          onClose={() => setIp4Dialog(null)}
        />
      )}

      {sourceDialog !== null && (
        <MasterkasaSourceDialog
          mode={sourceDialog.mode}
          source={sourceDialog.mode === 'settings' ? sourceDialog.source : null}
          onClose={() => setSourceDialog(null)}
        />
      )}

      <AlertDialog open={createdApiKey !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('settings.integrations.apiKeys.secret.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('settings.integrations.apiKeys.secret.warning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {createdApiKey?.apiKey !== undefined && (
            <div className="space-y-1.5">
              <Label>{t('settings.integrations.apiKeys.apiKey')}</Label>
              <div className="flex gap-2">
                <Input readOnly value={createdApiKey.apiKey} className="font-mono" />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => copy('apiKey', createdApiKey.apiKey!)}
                >
                  {copied === 'apiKey'
                    ? t('settings.integrations.copied')
                    : t('settings.integrations.copy')}
                </Button>
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setCreatedApiKey(null)}>
              {t('settings.integrations.apiKeys.secret.acknowledge')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={created !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('settings.integrations.secret.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('settings.integrations.secret.warning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {created !== null && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>{t('settings.integrations.clientId')}</Label>
                <div className="flex gap-2">
                  <Input readOnly value={created.id} className="font-mono" />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => copy('clientId', created.id)}
                  >
                    {copied === 'clientId'
                      ? t('settings.integrations.copied')
                      : t('settings.integrations.copy')}
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>{t('settings.integrations.clientSecret')}</Label>
                <div className="flex gap-2">
                  <Input readOnly value={created.secret} className="font-mono" />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => copy('secret', created.secret)}
                  >
                    {copied === 'secret'
                      ? t('settings.integrations.copied')
                      : t('settings.integrations.copy')}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setCreated(null)}>
              {t('settings.integrations.secret.acknowledge')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('settings.integrations.revoke.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('settings.integrations.revoke.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('settings.integrations.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={revoke} disabled={revokeClient.isPending}>
              {t('settings.integrations.revoke.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** Credential state is destroyed when licence, user or token changes. */
function SecureScaleCredentialCard() {
  const t = useT('admin_ui');
  const clients = useServiceClients();
  const create = useCreateServiceClient();
  const revokeClient = useRevokeServiceClient();
  const [revokeTarget, setRevokeTarget] = useState<ServiceClient | null>(null);
  const [name, setName] = useState('');
  const [created, setCreated] = useState<CreatedServiceClient | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (name.trim() === '' || create.isPending) return;
    setError(null);
    setCopied(null);
    try {
      setCreated(await create.mutateAsync({ name: name.trim(), scopes: ['securescale.read'] }));
      setName('');
    } catch (cause) {
      setError(errorMessage(cause, t('settings.integrations.create.error')));
    }
  }
  async function copy(label: string, value: string) {
    if (await copyValue(value)) setCopied(label);
  }
  async function revoke() {
    if (revokeTarget === null) return;
    setError(null);
    try {
      await revokeClient.mutateAsync(revokeTarget.id);
      setRevokeTarget(null);
    } catch (cause) {
      setError(errorMessage(cause, t('settings.integrations.revoke.error')));
    }
  }
  return (
    <section className="space-y-5 rounded-lg border border-border bg-card p-5">
      <div>
        <h2 className="text-base font-semibold text-primary">
          {t('settings.integrations.securescale.title')}
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          {t('settings.integrations.securescale.description')}
        </p>
      </div>
      <form onSubmit={submit} className="flex max-w-xl items-end gap-2">
        <div className="flex-1 space-y-1.5">
          <Label htmlFor="securescale-client-name">
            {t('settings.integrations.securescale.name')}
          </Label>
          <Input
            id="securescale-client-name"
            value={name}
            maxLength={100}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <Button disabled={name.trim() === '' || create.isPending}>
          {create.isPending
            ? t('settings.integrations.credential.creating')
            : t('settings.integrations.securescale.create')}
        </Button>
      </form>
      {error !== null && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {clients.isLoading ? (
        <p>{t('settings.integrations.credentials.loading')}</p>
      ) : clients.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {t('settings.integrations.credentials.error')}
        </p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>{t('settings.integrations.credential.name')}</TH>
              <TH>{t('settings.integrations.clientId')}</TH>
              <TH>{t('settings.integrations.status')}</TH>
              <TH>{t('settings.integrations.actions')}</TH>
            </TR>
          </THead>
          <TBody>
            {(clients.data ?? [])
              .filter((client) => client.scopes.includes('securescale.read'))
              .map((client) => (
                <TR key={client.id}>
                  <TD>{client.name}</TD>
                  <TD className="font-mono text-xs">{client.id}</TD>
                  <TD>
                    {client.status === 'active'
                      ? t('settings.integrations.status.active')
                      : t('settings.integrations.status.revoked')}
                  </TD>
                  <TD>
                    {client.status === 'active' && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setRevokeTarget(client)}
                      >
                        {t('settings.integrations.revoke')}
                      </Button>
                    )}
                  </TD>
                </TR>
              ))}
            {(clients.data ?? []).every(
              (client) => !client.scopes.includes('securescale.read'),
            ) && (
              <TR>
                <TD colSpan={4}>{t('settings.integrations.securescale.empty')}</TD>
              </TR>
            )}
          </TBody>
        </Table>
      )}
      <AlertDialog open={created !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('settings.integrations.secret.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('settings.integrations.secret.warning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {created !== null && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="securescale-client-id">{t('settings.integrations.clientId')}</Label>
                <div className="flex gap-2">
                  <Input
                    id="securescale-client-id"
                    readOnly
                    value={created.id}
                    className="font-mono"
                  />
                  <Button type="button" variant="outline" onClick={() => copy('id', created.id)}>
                    {copied === 'id'
                      ? t('settings.integrations.copied')
                      : t('settings.integrations.copy')}
                  </Button>
                </div>
              </div>
              <div>
                <Label htmlFor="securescale-client-secret">
                  {t('settings.integrations.clientSecret')}
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="securescale-client-secret"
                    readOnly
                    value={created.secret}
                    className="font-mono"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => copy('secret', created.secret)}
                  >
                    {copied === 'secret'
                      ? t('settings.integrations.copied')
                      : t('settings.integrations.copy')}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => {
                setCreated(null);
                setCopied(null);
              }}
            >
              {t('settings.integrations.secret.acknowledge')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('settings.integrations.revoke.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('settings.integrations.revoke.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('settings.integrations.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={revoke} disabled={revokeClient.isPending}>
              {t('settings.integrations.revoke.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
